import {
	type FinancialStatementPeriodId,
	type FinancialViewRow,
	type StatementMatrixViewModel,
} from "../../../ui/FinancialDataGrid";
import type { FinancialPeriodDto } from "../../utils";

/**
 * The set of pluggable statement data sources the analysis metrics can read from. Adding a source is
 * a three-step change with no churn elsewhere: add a key here, register it in the parent grid via
 * {@link statementSource}, and have metrics declare it as required (see analysisMetrics `metric`).
 */
export const ANALYSIS_SOURCE_KEYS = ["balanceSheet", "incomeStatement", "wip", "debt"] as const;
export type AnalysisSourceKey = (typeof ANALYSIS_SOURCE_KEYS)[number];

/** Indexes every finite allowed value in a statement column by its row id, for metric lookups. */
export const buildAllowedByRowId = <TId extends FinancialStatementPeriodId>(
	rows: FinancialViewRow<TId>[],
	statementId: TId,
): Map<string, number> => {
	const byRowId = new Map<string, number>();
	const walk = (rs: FinancialViewRow<TId>[]): void => {
		for (const row of rs) {
			const allowed = row.cells[statementId]?.allowed;
			if (typeof allowed === "number" && Number.isFinite(allowed)) {
				byRowId.set(row.id, allowed);
			}
			if (row.children) walk(row.children);
		}
	};
	walk(rows);
	return byRowId;
};

/**
 * A statement source bound to its (optional) view model. `lookupForPeriod` yields a rowId → allowed
 * value map for a period, or null when the period has no active statement of this kind (or the source
 * has no view model yet).
 */
export type AnalysisSourceLookup = {
	key: AnalysisSourceKey;
	lookupForPeriod: (period: FinancialPeriodDto) => Map<string, number> | null;
};

/**
 * Bind a statement source to a concretely-typed view model + the accessor for its active statement id
 * on a period. A null view model (e.g. WIP/Debt before their data is wired) yields an always-empty
 * source, so metrics that require it resolve to "--" until the data lands.
 */
export const statementSource = <
	TId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
>(
	key: AnalysisSourceKey,
	viewModel: StatementMatrixViewModel<TId> | null,
	activeStatementId: (period: FinancialPeriodDto) => TId | null,
): AnalysisSourceLookup => ({
	key,
	lookupForPeriod: (period) => {
		if (viewModel === null) return null;
		const id = activeStatementId(period);
		return id === null ? null : buildAllowedByRowId(viewModel.rows, id);
	},
});
